import { CodeEditor } from '@acrodata/code-editor';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { AngularSplitModule } from 'angular-split';
import { FileUploadModule } from 'ng2-file-upload';

import { ImportDialogComponent } from 'app/components/import-dialog/import-dialog.component';
import { EvaluationService } from 'app/providers/semantria-api/evaluation-service';
import { IndustryPackService } from 'app/providers/semantria-api/industry-pack.service';
import { GroupRouteGuardService } from 'app/services/group-route-guard.service';
import { AppSharedModule } from '../../app-shared.module';
import {
  AuthGuardService,
  NotExecutiveGuardService,
} from '../../providers/bridge-api/auth.service';
import { ConfigurationService } from '../../providers/semantria-api/configuration.service';
import { LanguageTemplateService } from '../../providers/semantria-api/language-template.service';
import { SemantriaAccountService } from '../../providers/semantria-api/semantria-account.service';
import { SemantriaFeaturesService } from '../../providers/semantria-api/semantria-features.service';
import { SemantriaUsersService } from '../../providers/semantria-api/semantria-users.service';
import { ConfigurationTesterService } from '../../providers/storage-api/configurations.service';
import { AddConfigurationLayerDialogComponent } from './components/add-configuration-layer-dialog/add-configuration-layer-dialog.component';
import { ConfigurationActivityTableComponent } from './components/configuration-activity-table/configuration-activity-table.component';
import { ConfigurationBlacklistComponent } from './components/configuration-blacklist/configuration-blacklist.component';
import { ConfigurationCollectionSelectorComponent } from './components/configuration-collection-selector/configuration-collection-selector.component';
import { ConfigurationConceptTopicsComponent } from './components/configuration-concept-topics/configuration-concept-topics.component';
import { ConfigurationContentFormComponent } from './components/configuration-content-form/configuration-content-form.component';
import { ConfigurationEntitiesComponent } from './components/configuration-entities/configuration-entities.component';
import { ConfigurationGeneralComponent } from './components/configuration-general/configuration-general.component';
import { ConfigurationItemEditorComponent } from './components/configuration-item-editor/configuration-item-editor.component';
import { ConfigurationItemEditorOptionsPopoutComponent } from './components/configuration-item-editor-options-popout/configuration-item-editor-options-popout.component';
import { ConfigurationLayersComponent } from './components/configuration-layers/configuration-layers.component';
import { ConfigurationQueryTopicsComponent } from './components/configuration-query-topics/configuration-query-topics.component';
import { ConfigurationQueryTopicsPurposeDialogComponent } from './components/configuration-query-topics-purpose-dialog/configuration-query-topics-purpose-dialog.component';
import { ConfigurationSentimentComponent } from './components/configuration-sentiment/configuration-sentiment.component';
import { ConfigurationSettingsDrawerComponent } from './components/configuration-settings-drawer/configuration-settings-drawer.component';
import { ConfigurationTaxonomyComponent } from './components/configuration-taxonomy/configuration-taxonomy.component';
import { ConfigurationTesterDocumentListComponent } from './components/configuration-tester-document-list/configuration-tester-document-list.component';
import { CreateConfigurationDrawerComponent } from './components/create-configuration-drawer/create-configuration-drawer.component';
import { ImportConfigurationConfirmDialogComponent } from './components/import-configuration-confirm-dialog/import-configuration-confirm-dialog.component';
import { ImportIntoConfigurationDrawerComponent } from './components/import-into-configuration-drawer/import-into-configuration-drawer.component';

import { TestConfigurationDrawerComponent } from './components/test-configuration-drawer/test-configuration-drawer.component';
import { TestDocumentGeneratorDialogComponent } from './components/test-document-generator-dialog/test-document-generator-dialog.component';
import { ConfigurationSharedModule } from './configuration-shared.module';

import { ConfigurationDetailsComponent } from './pages/configuration-details/configuration-details.component';
import { ConfigurationListComponent } from './pages/configuration-list/configuration-list.component';
import { ConfigurationListDetailsComponent } from './pages/configuration-list-details/configuration-list-details.component';
import { CanDeactivateQueryTopicsService } from './services/can-deactivate-query-topics.service';
import { ConfigurationVersionTrackerService } from './services/configuration-version-tracker.service';
import { TaxonomyTargetWatcherService } from './services/taxonomy-target-watcher.service';

@NgModule({
  declarations: [
    ConfigurationListComponent,
    ConfigurationDetailsComponent,
    ConfigurationQueryTopicsComponent,
    ConfigurationQueryTopicsPurposeDialogComponent,
    ConfigurationItemEditorComponent,
    TestDocumentGeneratorDialogComponent,
    ConfigurationSentimentComponent,
    ConfigurationConceptTopicsComponent,
    ConfigurationEntitiesComponent,
    ConfigurationTaxonomyComponent,
    ConfigurationBlacklistComponent,
    CreateConfigurationDrawerComponent,
    ConfigurationContentFormComponent,
    ImportIntoConfigurationDrawerComponent,
    ImportConfigurationConfirmDialogComponent,
    ConfigurationLayersComponent,
    AddConfigurationLayerDialogComponent,
    TestConfigurationDrawerComponent,
    ConfigurationSettingsDrawerComponent,
    ConfigurationGeneralComponent,
    ConfigurationListDetailsComponent,
    ConfigurationActivityTableComponent,
  ],
  imports: [
    AppSharedModule,
    ConfigurationSharedModule,
    ImportDialogComponent,
    ScrollingModule,
    NgbPopoverModule,
    AngularSplitModule,
    CodeEditor,
    FileUploadModule,
    ConfigurationItemEditorOptionsPopoutComponent,
    ConfigurationCollectionSelectorComponent,
    ConfigurationTesterDocumentListComponent,
    RouterModule.forRoot(
      [
        {
          canActivate: [AuthGuardService, NotExecutiveGuardService, GroupRouteGuardService],
          path: 'groups/:groupId/configurations',
          component: ConfigurationListComponent,

          children: [{ path: 'summary/:id', component: ConfigurationListDetailsComponent }],
        },
        {
          canActivate: [AuthGuardService, NotExecutiveGuardService, GroupRouteGuardService],
          path: 'groups/:groupId/configurations/:id',
          component: ConfigurationDetailsComponent,
          canDeactivate: [CanDeactivateQueryTopicsService],
          runGuardsAndResolvers: 'always',
        },
      ],
      {}
    ),
  ],
  providers: [
    ConfigurationService,
    ConfigurationTesterService,
    EvaluationService,
    SemantriaAccountService,
    SemantriaFeaturesService,
    SemantriaUsersService,
    LanguageTemplateService,
    IndustryPackService,
    CanDeactivateQueryTopicsService,
    TaxonomyTargetWatcherService,
    ConfigurationVersionTrackerService,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ConfigurationModule {}
